import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import NewsletterForm from "@/components/NewsletterForm";
import { trackEvent } from "@/lib/analytics";
import { X, Sparkles, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "fcv_welcome_popup_seen_v1";
const PROMO_CODE = "WELCOME10";
const DELAY_MS = 8000;

export default function WelcomePopup() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(() => {
      setOpen(true);
      trackEvent("ViewContent", { content_name: "welcome_popup" });
    }, DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      toast.success("Code copié dans le presse-papier");
      trackEvent("Lead", { content_name: "promo_code_copied", value: 10, currency: "EUR" });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Impossible de copier le code");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-[#2C2420]/55 backdrop-blur-sm animate-fade-up px-4"
      role="dialog"
      aria-modal="true"
      data-testid="welcome-popup"
    >
      <div className="relative w-full max-w-md md:max-w-lg bg-[#FDFBF9] rounded-3xl overflow-hidden shadow-2xl border border-[#EADBD1]">
        <button
          onClick={close}
          aria-label="Fermer"
          className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-white border border-[#EADBD1] hover:border-[#2C2420] grid place-items-center text-[#2C2420] transition-colors"
          data-testid="welcome-popup-close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Decorative band */}
        <div className="bg-[#2C2420] text-[#FDFBF9] px-7 pt-9 pb-7 text-center relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#C58C85]/30 via-transparent to-transparent pointer-events-none" />
          <Sparkles className="h-7 w-7 text-[#C58C85] mx-auto" strokeWidth={1.5} />
          <p className="text-xs uppercase tracking-[0.3em] text-[#C58C85] font-medium mt-3">Offre de bienvenue</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3 leading-tight">
            <span className="italic text-[#C58C85]">−10%</span> sur votre
            <br />
            première commande
          </h2>
        </div>

        <div className="px-7 py-8">
          <p className="text-sm text-[#6B5D54] text-center max-w-sm mx-auto">
            Inscrivez-vous à notre newsletter et utilisez le code ci-dessous au moment du paiement.
          </p>

          <button
            onClick={copy}
            className="mt-5 w-full rounded-2xl border-2 border-dashed border-[#C58C85] bg-[#F5EFE6] hover:bg-[#EADBD1] transition-colors py-4 flex items-center justify-center gap-3"
            data-testid="welcome-popup-copy-code"
          >
            <span className="font-mono font-medium text-2xl tracking-[0.2em] text-[#2C2420]">{PROMO_CODE}</span>
            {copied ? <Check className="h-5 w-5 text-[#25D366]" /> : <Copy className="h-4 w-4 text-[#6B5D54]" />}
          </button>
          <p className="text-[11px] text-[#6B5D54] text-center mt-2">Cliquez pour copier le code</p>

          <div className="mt-6 flex flex-col items-center">
            <NewsletterForm source="welcome-popup" />
          </div>

          <Button
            onClick={() => { close(); window.scrollTo({ top: document.getElementById("produit")?.offsetTop, behavior: "smooth" }); }}
            className="mt-5 w-full rounded-full bg-[#2C2420] hover:bg-[#3a2f2a] text-white h-12"
            data-testid="welcome-popup-shop-btn"
          >
            Je découvre les 67 teintes
          </Button>

          <button
            onClick={close}
            className="mt-3 w-full text-xs text-[#6B5D54] hover:text-[#2C2420] transition-colors"
            data-testid="welcome-popup-dismiss"
          >
            Non merci, je préfère payer plein tarif
          </button>
        </div>
      </div>
    </div>
  );
}
